import "./payment-plan.scss"
import React, {useContext, useEffect, useState} from "react";
import {PlanInformation} from "./PlanInformation/PlanInformation";
import customAxios from "../../../../service/api/interceptors";
import {PaymentPlanItems} from "./PaymentPlanItems/PaymentPlanItems";
import {PaymentPlanCredits} from "./PaymentPlanCredits/PaymentPlanCredits";
import {useHistory, useLocation} from "react-router-dom";
import {CompanyType} from "../../../../types/company.type";
import ModalContainer from "../../../../components/ModalContainer";
import logoIcon from "../../../../assets/images/logo.svg";
import {MainContextProvider} from "../../../../context/MainContext";
import {PaymentSuccessfulModal} from "./PaymentSuccessfulModal/PaymentSuccessfulModal";
import {useIntercom} from "react-use-intercom";
import {PaymentPlanNewItems} from "./PaymentPlanItems/PaymentPlanNewItems";
import {toastifyError} from "../../../../service/toastify/toastify";
import PaymentPlanTable from "./PaymentPlanTable/PaymentPlanTable";
import {CreditTransactionsHistory} from "./CreditTransactionsHistory/CreditTransactionsHistory";

export type CreditsBundle = {
    id: number,
    subscription_id: number,
    title: string,
    description: string,
    count: number,
    price: number,
    created_at: number,
    contract_extension_months: number
    discount_percents: number
    range_count_from: number
    range_count_to: number
    range_price_per_credit: number
}

export type creditRangeBundlesType = {
    contract_extension_months: number
    discount_percents: number
    range_count_from: number
    range_count_to: number
    range_price_per_credit: number
}

export const PaymentPlan = () => {
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const { showNewMessages } = useIntercom()
    const { setLoading, creditCount } = useContext(MainContextProvider)
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [credits, setCredits] = useState(1);
    const [finishDate, setFinishDate] = useState<string | null>(null)
    const history = useHistory()
    const [companies, setCompanies] = useState<CompanyType[]>([])
    const [subscription, setSubscription] = useState<{ [key: string]: string }>({})
    const [currentPlanIndex, setCurrentPlanIndex] = useState(0)
    const [upcomingPlanIndex, setUpcomingPlanIndex] = useState(-1)
    const [upcomingPlan, setUpcomingPlan] = useState<{ [key: string]: string } | null>(null)
    const [planIndex, setPlanIndex] = useState(0)
    const [creditBundle, setCreditBundle] = useState<CreditsBundle[]>([])
    const [creditSum, setCreditSum] = useState(0)
    const [creditRangeBundles, setCreditRangeBundles] = useState<creditRangeBundlesType[]>([])
    const [creditCounts, setCreditCounts] = useState<{ count: string, expired_at: string, type: number }[]>([])
    const [isRollingSubscription, setIsRollingSubscription] = useState(false)
    const [isShowCreditTransactionHistory, setIsShowCreditTransactionHistory] = useState(false)
    const [isPayAsYouGo, setIsPayAsYouGo] = useState(false)
    const [companyCredits, setCompanyCredits] = useState({
        total_count: 0,
        covered_by_monthly_plan: 0,
        purchased: 0,
        expiration_covered_by_monthly_plan: 0,
        expiration_purchased: 0,
    })

    const getFinishDate = async (company_id: string) => {
        try {
            const { data } = await customAxios.get(`company/${company_id}/subscription/expiration-date`)
            if (data?.expiration_date) setFinishDate(data.expiration_date)
        } catch (e) {}
    }

    const getData = async () => {
        setLoading(true)
        try {
            const { data: companies, status: companiesStatus } = await customAxios.get(`/company/company/get-companies`)
            const { data: allSubscriptions } = await customAxios.get(`/user/client/info/subscriptions`)
            const { data: subscriptionData, status: subscriptionsStatus } = await customAxios.get(`/company/${companies[0].id}/subscription`)
            const { data: credits, status: creditsStatus } = await customAxios.get(`/company/${companies[0].id}/credits`)
            const { data: creditCountsData, status: creditCountsStatus } = await customAxios.get(`/company/${companies[0].id}/credits-by-date?type=0`)
            const { data: creditCountsData2 } = await customAxios.get(`/company/${companies[0].id}/credits-by-date?type=1`)
            const { data: creditBundleItems } = await customAxios.get(`/subscription/${subscriptionData.id}/credit-range-price`)
            setCreditRangeBundles(creditBundleItems.sort((a: creditRangeBundlesType, b: creditRangeBundlesType) => a.range_count_from - b.range_count_from))

            /*setCreditRangeBundles([
                creditBundleItems[0],
                { ...creditBundleItems[1], contract_extension_months: 1, discount_percents: 10 },
                { ...creditBundleItems[2], contract_extension_months: 3, discount_percents: 20 },
                { ...creditBundleItems[3], contract_extension_months: 12, discount_percents: 50 },
            ])*/

            if (creditCountsStatus === 200) {
                setCreditCounts([...creditCountsData.map((el: any) => ({ ...el, type: 0 })), ...creditCountsData2.map((el: any) => ({ ...el, type: 1 }))])
            }

            const findCurrentPlanIndex = allSubscriptions.findIndex((el: { active: number }) => el.active === 1)
            if (findCurrentPlanIndex >= 0) {
                setCurrentPlanIndex(findCurrentPlanIndex)
                setSubscription(allSubscriptions[findCurrentPlanIndex])
            }

            const findUpcomingPlanIndex = allSubscriptions.findIndex((el: { active: number }) => el.active === 2)
            if (findUpcomingPlanIndex >= 0) {
                setUpcomingPlan(allSubscriptions[findUpcomingPlanIndex])
                setUpcomingPlanIndex(findUpcomingPlanIndex)
            }

            if (creditsStatus === 200) {
                setCompanies(companies)
                if (companies.length > 0) {
                    await getFinishDate(companies[0].id)
                }
            }

            if (subscriptionsStatus === 200 && subscriptionData) {
                setSubscription(subscriptionData)
                setIsPayAsYouGo(+subscriptionData.is_pay_as_you_go === 1)
                setCreditSum(subscriptionData.purchased_credit_price)
                const { data: creditsBundleData } = await customAxios.get(`/subscription/${subscriptionData.id}/credit-bundles`)
                setCreditBundle(creditsBundleData)
                setIsRollingSubscription(+subscriptionData.is_rolling_subscription === 1)
                if (subscriptionData.is_rolling_subscription === "0") {
                    const { data: creditCountsData3 } = await customAxios.get(`/company/${companies[0].id}/credits-by-date`)
                    setCreditCounts(creditCountsData3)
                }
            }

            if (creditsStatus === 200) {
                setCompanyCredits({ ...credits })
                // setCredits({ ...credits })
            }
        } catch (e) {}
        setLoading(false)
    }

    const createInvoiceFromUrl = async () => {
        const count = params.get('count');
        const id = params.get('id');

        if (id) {
            if (creditBundle.length === 0) {
                return
            }
            const creditBundleItem = creditBundle.find((el: CreditsBundle) => +el.id === +id)
            if (!creditBundleItem) {
                toastifyError("Invalid credit bundle id")
                return
            }
        }

        setLoading(true)
        if (count || id) {
            const { status, data } = await customAxios.post(`/invoices/company/${companies[0]?.id}/create-credit?isBundle=${!!id}`, {
                credits_count: count,
                credit_bundle_id: id
            })

            if (status === 200) {
                history.push(`/checkout/${data.id}?page-type=credit`)
            }
        }
        setLoading(false)
    }

    useEffect(() => {
        if (companies.length > 0) {
            createInvoiceFromUrl()
        }
    }, [companies, creditBundle]);

    useEffect(() => {
        document.title = 'Manage My Plan & Credits'
        getData().then()
    }, [])

    const createCreditInvoice = async (key: number, isBundle?: boolean, credit_bundle_id?: number) => {
        setLoading(true)
        try {
            const { status, data } = await customAxios.post(`/invoices/company/${companies[0]?.id}/create-credit?isBundle=${isBundle}`, {
                credits_count: key,
                credit_bundle_id: credit_bundle_id
            })

            if (status === 200) {
                history.push(`/checkout/${data.id}?page-type=credit`)
            }
        } catch (e) {}
        setLoading(false)
    }
    
    const cancelPlan = async () => {
        setLoading(true)
        const {} = await customAxios.post(`subscription/${subscription.id}/unsubscribe`)
        setLoading(false)
    }

    return (
        <div className="payment-plan">
            <h2>Manage My Contract & Credits</h2><></>
            <div className={'payment-plan__naming'}>
                <div className="payment-plan__naming-name">
                    <div className="payment-plan__tabs">
                        <div onClick={() => setPlanIndex(0)} className={`${planIndex === 0 && 'payment-plan__tab-active'} payment-plan__tab`}>Current Contract</div>
                        {upcomingPlan && <div onClick={() => setPlanIndex(upcomingPlanIndex)} className={`${planIndex === upcomingPlanIndex && 'payment-plan__tab-active'} payment-plan__tab`}>Upcoming Contract</div>}
                    </div>
                </div>
                <div className="payment-plan__naming-contact-us">
                    Have questions about your contract? <a onClick={() => showNewMessages('')}>Contact Us</a>
                </div>
            </div>
            <PlanInformation
                companyCredits={companyCredits}
                plan_id={planIndex === 0 ? subscription?.id : (upcomingPlan?.id || '')}
                plan_name={planIndex === 0 ? subscription?.title : (upcomingPlan?.title || '')}
                payment_period={planIndex === 0 ? (subscription?.payment_period || '1') : (upcomingPlan?.payment_period || '1')}
                plan_date={finishDate}
                cancelPlan={cancelPlan}
                updateSubscriptions={getData}
                is_sub_paused={planIndex === 0 ? (+subscription?.is_sub_paused === 1 ? "1" : "0") : (+(upcomingPlan?.is_sub_paused || 1) === 1 ? "1" : "0")}
                showNewMessages={(key: string) => showNewMessages(key)}
                isUpcomingTab={planIndex === upcomingPlanIndex}
                isUpcoming={!!upcomingPlan}
                isSubscriptionActive={+subscription.active === 1}
                creditCounts={creditCounts}
                isRollingSubscription={isRollingSubscription}
                isPayAsYouGo={isPayAsYouGo}
            />
            <div className="w100">
                <div className="follow-up-tabs">
                    <div onClick={() => setIsShowCreditTransactionHistory(false)} className={`${!isShowCreditTransactionHistory && 'follow-up-tabs__active-item'} follow-up-tabs__item`}>
                        Contract & Credits Top-Up
                    </div>
                    <div onClick={() => setIsShowCreditTransactionHistory(true)} className={`${isShowCreditTransactionHistory && 'follow-up-tabs__active-item'} follow-up-tabs__item`}>
                        Credits Transaction History
                    </div>
                </div>
            </div>
            {isShowCreditTransactionHistory && companies.length > 0 && <CreditTransactionsHistory companyId={+companies[0]?.id} />}
            {!isShowCreditTransactionHistory && <div>
                {/*<PaymentPlanItems
                // subscriptionTitle={planIndex === 0 ? subscription?.title : (upcomingPlan?.title || '')}
                upcomingTitle={upcomingPlan?.title || ""}
                subscriptionTitle={subscription?.title}
                isUpcomingTab={planIndex === upcomingPlanIndex}
                isUpcomingPlan={!!upcomingPlan}
            />*/}
                <PaymentPlanCredits
                    creditsCount={credits}
                    updateCredits={(count: number) => setCredits(count)}
                    createInvoice={createCreditInvoice}
                    creditBundle={creditBundle}
                    creditSum={creditSum}
                    creditBundleItems={creditRangeBundles}
                />
                {!isRollingSubscription && creditCount.isCreditSubscription && <PaymentPlanTable/>}
                <InfoModal
                    isOpen={isModalOpen}
                    close={() => setIsModalOpen(false)}
                />
            </div>}
        </div>
    );
}


const InfoModal: React.FC<{ isOpen: boolean, close: () => void }> = ({ isOpen, close }) => {
    return (
        <div className={'payment-plan-information-modal'}>
            <ModalContainer isOpen={isOpen} close={close}>
                <div>
                    <div className={'payment-plan-information-modal__logo'}>
                        <img src={logoIcon} alt=""/>
                    </div>
                    <div className={'payment-plan-information-modal__text'}>
                        <h3>How can I use my credits?</h3>
                        <p>
                            Use your Credits to create new legal tasks. For your VLO to begin working on your legal task,
                            you must pay 1 Credit per task. Some tasks are simple and can be completed without additional cost.
                        </p>
                        <p>
                            For more complex legal tasks, your VLO will provide an estimate on any additional Credits that may be required.
                            You will be notified each time credits are deducted from your balance.
                        </p>
                        <h3>Credits included in your contract</h3>
                        <p>
                            Each month that your contract is active, you will receive a number of Credits to spend on your business project.
                            Make sure to use them within the month, as they cannot be transferred to the next month of your contract. Please check
                            the expiry dates and use your credits before they expire. We will always redeem your credits covered by your monthly plan
                            first before redeeming any additional credits that you have purchased.
                        </p>
                        <h3>What if my Credits balance is insufficient?</h3>
                        <p>
                            If you are unable to proceed with a task, you can buy additional Credits to top up your balance.
                            We recommend purchasing Credits in advance to avoid delays in processing your tasks. You can use our
                            Credit bundles to quickly top up your balance.
                        </p>
                        <button onClick={close} className={'btn-black w100 mt20'}>OK</button>
                    </div>
                </div>
            </ModalContainer>
        </div>
    )
}

// 25.05
// 29.06
// 10.08
// 31.08
//